import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { ArrowLeft, Play, Pause, Loader2, RotateCcw, Video } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CURRICULUM } from "@/lib/curriculum";
import { MOODS, JOURNEY, bedFor, changeOpacityFor } from "@/lib/journey";
import { useSceneBeds } from "@/lib/useSceneBeds";
import { useWorkbookAnswers } from "@/lib/useWorkbookAnswers";
import { ModuleShareDialog } from "@/components/ModuleShare";
import { CinematicScene } from "@/components/cinematic/CinematicScene";
import { CinematicTopBar } from "@/components/cinematic/CinematicTopBar";
import { GuideHeader } from "@/components/cinematic/GuideHeader";
import { Teleprompter } from "@/components/cinematic/Teleprompter";
import { ReflectionWorkbook } from "@/components/cinematic/ReflectionWorkbook";
import { WorkItCard } from "@/components/cinematic/WorkItCard";
import { CinematicLocked } from "@/components/cinematic/CinematicLocked";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const splitLines = (t) => t.split(/(?<=[.!?])\s+/).filter(Boolean);

const PLAY_ICONS = {
  loading: <Loader2 size={22} className="animate-spin" />,
  playing: <Pause size={22} fill="currentColor" />,
  done: <RotateCcw size={22} />,
};

export default function CinematicModulePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [phase, setPhase] = useState("idle"); // idle | loading | playing | paused | done
  const [lineIdx, setLineIdx] = useState(-1);
  const [markedDone, setMarkedDone] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [progress, setProgress] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [bedsOn, setBedsOn] = useState(true);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const trackedRef = useRef(false);

  const idx = CURRICULUM.findIndex((m) => m.id === id);
  const mod = idx >= 0 ? CURRICULUM[idx] : null;
  const next = idx >= 0 && idx < CURRICULUM.length - 1 ? CURRICULUM[idx + 1] : null;
  const [adversityScene, changeScene] = JOURNEY[id] || [id, id];
  const changeOpacity = changeOpacityFor(id, progress);
  const liveMood = changeOpacity > 0.5 ? "Change" : (MOODS[id] || "Ground");
  const { startBeds, pauseBeds, stopBeds } = useSceneBeds(bedFor(adversityScene), bedFor(changeScene), changeOpacity, bedsOn);

  const lines = useMemo(() => (mod ? splitLines(mod.narration) : []), [mod]);
  const fractions = useMemo(() => {
    const total = lines.reduce((a, l) => a + l.length, 0) || 1;
    let acc = 0;
    return lines.map((l) => { acc += l.length; return acc / total; });
  }, [lines]);

  const owns = (user && user.entitlements) || [];
  const isAdmin = user?.role === "admin";
  const entitled = isAdmin || ["bundle", "course", "modules"].some((k) => owns.includes(k));
  const { answers, saveState, onAnswerChange } = useWorkbookAnswers(id, entitled && !!mod);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    stopBeds();
  };
  const stopAudioRef = useRef(stopAudio);
  stopAudioRef.current = stopAudio;

  useEffect(() => {
    setPhase("idle"); setLineIdx(-1); setMarkedDone(false); setProgress(0); trackedRef.current = false;
    return () => stopAudioRef.current();
  }, [id]);

  useEffect(() => {
    if (!entitled || !mod) return undefined;
    let alive = true;
    axios.get(`${API}/progress/me`).then((r) => {
      if (alive && r.data?.completed_modules?.includes(id)) setMarkedDone(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [entitled, mod, id]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/modules/videos/index`).then((r) => {
      if (alive) setHasVideo((r.data?.available || []).includes(id));
    }).catch(() => {});
    return () => { alive = false; };
  }, [id]);

  const play = async () => {
    if (audioRef.current && phase === "playing") { audioRef.current.pause(); return; }
    if (audioRef.current && (phase === "paused" || phase === "done")) {
      if (phase === "done") { audioRef.current.currentTime = 0; setLineIdx(-1); setProgress(0); }
      audioRef.current.play();
      return;
    }
    setPhase("loading");
    if (!trackedRef.current) {
      trackedRef.current = true;
      axios.post(`${API}/analytics/preview`, { kind: "module", id }).catch(() => {});
    }
    try {
      const res = await axios.post(`${API}/tts/generate`, { text: mod.narration }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const a = new Audio(url);
      a.onplay = () => { setPhase("playing"); startBeds(); };
      a.onpause = () => { if (!a.ended) { setPhase("paused"); pauseBeds(); } };
      a.onended = () => { setPhase("done"); setLineIdx(lines.length); setProgress(1); };
      a.ontimeupdate = () => {
        if (!a.duration) return;
        const f = a.currentTime / a.duration;
        setProgress(f);
        const i = fractions.findIndex((fr) => f < fr);
        setLineIdx(i === -1 ? lines.length - 1 : i);
      };
      a.onerror = () => setPhase("idle");
      audioRef.current = a;
      await a.play();
    } catch {
      setPhase("idle");
    }
  };

  const markDone = async () => {
    try {
      await axios.post(`${API}/progress/modules/${id}`, { done: true });
      setMarkedDone(true);
    } catch (e) { console.warn("Mark done failed:", e); }
  };

  if (!mod) {
    return (
      <main className="min-h-screen pt-32 flex items-center justify-center" data-testid="cinematic-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl mb-4">Module not found.</h1>
          <Link to="/dashboard" className="btn-outline text-sm mx-auto w-fit"><ArrowLeft size={14} /> Dashboard</Link>
        </div>
      </main>
    );
  }

  if (user === null) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="cinematic-loading">
        <Loader2 size={24} className="animate-spin text-[#B45309]" />
      </main>
    );
  }

  const playing = phase === "playing";

  return (
    <main className="relative min-h-screen overflow-hidden" data-testid="cinematic-page">
      <CinematicScene adversityScene={adversityScene} changeScene={changeScene} changeOpacity={changeOpacity} />

      <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-24 min-h-screen flex flex-col">
        <CinematicTopBar liveMood={liveMood} bedsOn={bedsOn} onToggleBeds={() => setBedsOn((v) => !v)} />
        <GuideHeader mod={mod} playing={playing} />
        <Teleprompter lines={lines} lineIdx={lineIdx} phase={phase} />

        {entitled ? (
          <div className="flex flex-col items-center gap-8">
            <button
              onClick={play}
              disabled={phase === "loading"}
              data-testid="cinematic-play"
              className="w-16 h-16 rounded-full bg-[#F59E0B] text-black flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-60 shadow-[0_20px_50px_-12px_rgba(245,158,11,0.6)]"
              aria-label={playing ? "Pause" : "Play"}
            >
              {PLAY_ICONS[phase] || <Play size={22} fill="currentColor" className="ml-1" />}
            </button>

            {hasVideo && (
              <Link to={`/modules/${id}/watch`} className="btn-outline text-xs justify-center" data-testid="cinematic-watch-video">
                <Video size={14} /> Watch the guided video
              </Link>
            )}

            {mod.questions && mod.questions.length > 0 && (
              <ReflectionWorkbook mod={mod} answers={answers} saveState={saveState} onAnswerChange={onAnswerChange} />
            )}

            <AnimatePresence>
              {phase === "done" && (
                <WorkItCard mod={mod} next={next} markedDone={markedDone} onMarkDone={markDone} onShare={() => setShareOpen(true)} />
              )}
            </AnimatePresence>
          </div>
        ) : (
          <CinematicLocked user={user} />
        )}
      </div>
      <ModuleShareDialog open={shareOpen} onOpenChange={setShareOpen} moduleId={id} firstName={(user?.name || "I").split(" ")[0]} />
    </main>
  );
}
